'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds, 
  type CalculatorConfig, 
  type ControlValues,
  type MultiColumnSelectControl,
  type TextControl,
  type SelectControl,
  type NumberControl,
  type SingleColumnSelectControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { LazyTabLabECharts } from '@repo/ui';
import type { EChartsOption } from 'echarts';

// Type for sunburst specific control values
export interface SunburstControlValues extends ControlValues {
  [ControlIds.Category]: string;
  [ControlIds.Value]: string;
  [ControlIds.Title]: string;
  [ControlIds.ShowLabels]: string;
  [ControlIds.Radius]: string;
}

// Define the controls for the sunburst chart
export const sunburstControls = {
  tabs: [
    {
      title: 'Data',
      controls: [
        {
          id: ControlIds.Category,
          type: ControlType.SingleColumnSelect,
          label: 'Category path column',
          required: true
        } satisfies SingleColumnSelectControl,
        {
          id: ControlIds.Value,
          type: ControlType.SingleColumnSelect,
          label: 'Value column',
          required: true,
          numericOnly: true
        } satisfies SingleColumnSelectControl,
        {
          id: ControlIds.Title,
          type: ControlType.Text,
          label: 'Chart title',
          required: false
        } satisfies TextControl
      ]
    },
    {
      title: 'Appearance',
      controls: [
        {
          id: ControlIds.ShowLabels,
          type: ControlType.Select,
          label: 'Show labels',
          options: [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' }
          ],
          defaultValue: 'true'
        } satisfies SelectControl,
        {
          id: ControlIds.Radius,
          type: ControlType.Number,
          label: 'Chart radius (%)',
          min: 50,
          max: 100,
          step: 5,
          defaultValue: 90
        } satisfies NumberControl
      ]
    }
  ]
};

// Helper function to build hierarchical data from flat data
function buildHierarchicalData(
  paths: string[],
  values: number[]
): { name: string; value?: number; children?: any[] }[] {
  const root: { [key: string]: any } = {};

  paths.forEach((path, index) => {
    const parts = path.split('/').map(p => p.trim());
    let current = root;

    parts.forEach((part, i) => {
      if (!current[part]) {
        current[part] = { name: part };
        if (i === parts.length - 1) {
          current[part].value = values[index];
        } else {
          current[part].children = {};
        }
      }
      current = current[part].children || current[part];
    });
  });

  // Convert the object structure to array structure
  function convertToArray(obj: { [key: string]: any }): any[] {
    return Object.values(obj).map(node => ({
      name: node.name,
      value: node.value,
      children: node.children ? convertToArray(node.children) : undefined
    }));
  }

  return convertToArray(root);
}

// Pure function to generate ECharts config for a sunburst chart
export function getSunburstEChartsConfig({
  data,
  title,
  showLabels,
  radius
}: {
  data: any[];
  title?: string;
  showLabels: boolean;
  radius: number;
}): EChartsOption {
  // Calculate relative radii based on the user-selected radius
  const scale = radius / 100;
  const levelRadii = {
    level1: {
      r0: `${0 * scale}%`,
      r: `${35 * scale}%`
    },
    level2: {
      r0: `${35 * scale}%`,
      r: `${70 * scale}%`
    },
    level3: {
      r0: `${70 * scale}%`,
      r: `${radius}%`
    }
  };

  return {
    title: {
      text: title || 'Sunburst Chart',
      left: 'center',
      top: 2,
      padding: [0, 0, 0, 0]
    },
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const value = params.value || 0;
        return `${params.name}: ${value.toFixed(2)}`;
      }
    },
    series: [
      {
        type: 'sunburst',
        data,
        radius: [0, `${radius}%`],
        label: {
          show: showLabels,
          rotate: 'radial'
        },
        itemStyle: {
          borderWidth: 1,
          borderColor: '#fff'
        },
        levels: [
          {},
          {
            r0: levelRadii.level1.r0,
            r: levelRadii.level1.r,
            itemStyle: {
              borderWidth: 2
            },
            label: {
              rotate: 'tangential'
            }
          },
          {
            r0: levelRadii.level2.r0,
            r: levelRadii.level2.r,
            label: {
              align: 'right'
            }
          },
          {
            r0: levelRadii.level3.r0,
            r: levelRadii.level3.r,
            label: {
              position: 'outside',
              padding: 3,
              silent: false
            },
            itemStyle: {
              borderWidth: 3
            }
          }
        ]
      }
    ]
  };
}

export const sunburstConfig: CalculatorConfig<CalculatorId.SunburstChart> = {
  id: CalculatorId.SunburstChart,
  type: CalculatorType.Graph,
  name: 'Sunburst Chart',
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'category', 
        name: 'Category', 
        values: [
          'Electronics/Smartphones/iPhone',
          'Electronics/Smartphones/Android',
          'Electronics/Laptops/Gaming',
          'Electronics/Laptops/Business',
          'Clothing/Men/Shirts',
          'Clothing/Men/Pants',
          'Clothing/Women/Dresses',
          'Clothing/Women/Shoes'
        ] 
      },
      { 
        id: 'sales', 
        name: 'Sales', 
        values: ['1200', '1000', '800', '600', '400', '350', '900', '750'] 
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.Category]: 'category',
    [ControlIds.Value]: 'sales',
    [ControlIds.Title]: 'Product Category Sales',
    [ControlIds.ShowLabels]: 'true',
    [ControlIds.Radius]: '90'
  },
  controls: sunburstControls,
  getInitialConfig: (data: TableDataJSON) => {
    const numericColumns = data.columns.filter(col => 
      col.values.every(val => !isNaN(Number(val)))
    );
    const nonNumericColumns = data.columns.filter(col => 
      !col.values.every(val => !isNaN(Number(val)))
    );

    return {
      [ControlIds.Category]: nonNumericColumns[0]?.id || data.columns[0]?.id,
      [ControlIds.Value]: numericColumns[0]?.id,
      [ControlIds.Title]: 'Sunburst Chart',
      [ControlIds.ShowLabels]: 'true',
      [ControlIds.Radius]: '90'
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const sunburstControls = controls as SunburstControlValues;
    
    if (!data?.columns?.length) {
      return null;
    }

    const categoryColumn = data.columns.find(col => col.id === sunburstControls[ControlIds.Category]);
    const valueColumn = data.columns.find(col => col.id === sunburstControls[ControlIds.Value]);

    if (!categoryColumn || !valueColumn) {
      return null;
    }

    const hierarchicalData = buildHierarchicalData(
      categoryColumn.values,
      valueColumn.values.map(v => Number(v))
    );

    return getSunburstEChartsConfig({
      data: hierarchicalData,
      title: sunburstControls[ControlIds.Title],
      showLabels: sunburstControls[ControlIds.ShowLabels] === 'true',
      radius: Number(sunburstControls[ControlIds.Radius])
    });
  },
  render: ({ visualizationData }) => 
    visualizationData ? (
      <LazyTabLabECharts option={visualizationData} className="h-full w-full" />
    ) : null
}; 